import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "redux/selectors/user";
import useNavigate from "./useNavigate";
import { PATHS, SEARCH_PARAMS } from "constants/routes";
import { useLoading } from "providers/loading";
import { apiGetMe, apiLogoutUser } from "services/api/user";
import { decodeBase64, encodeBase64 } from "helpers/base64";
import { logoutUser, setUser } from "redux/actions/user";
import { SOURCING_DOMAIN } from "constants/schemas";
import { setChatList } from "redux/actions/chat";
import { addUtmParam } from "helpers/search";
import {
  User,
  UserPlan,
  UserBalance,
  BuyerData,
  SupplierData,
} from "types/user";

interface UseAuthReturn {
  isLoggedIn: boolean;
  currentBalance: number;
  isPaymentUser: boolean;
  getLoginUrl: string;
  user: User | null;
  userPlan: UserPlan | null;
  buyerData: BuyerData | null;
  supplierData: SupplierData | null;
  balance: UserBalance | null;
  onDirectLogin: () => void;
  onDirectSignup: () => void;
  onLogout: () => Promise<void>;
  onGetUserInformation: () => Promise<void>;
  onDirectNextPageOfLogin: () => void;
  onClickLoginSupplier: () => void;
}

const cleanRedirectUrl = (url: string): string => {
  try {
    // First check if it's already a valid path
    if (url.startsWith('/')) {
      return url;
    }
    
    // Try to decode if it's base64 encoded
    let decodedUrl;
    try {
      decodedUrl = decodeBase64(url);
    } catch (decodeError) {
      // If decode fails, assume it's already a plain URL
      decodedUrl = url;
    }
    
    // If it's already a path, return it
    if (decodedUrl.startsWith('/')) {
      return decodedUrl;
    }
    
    // If it's a full URL, extract the path with query params and hash
    if (decodedUrl.startsWith('http')) {
      const urlObj = new URL(decodedUrl);
      const fullPath = urlObj.pathname + urlObj.search + urlObj.hash;
      return fullPath;
    }
    
    // Default fallback - ensure it starts with /
    return decodedUrl.startsWith('/') ? decodedUrl : `/${decodedUrl}`;
  } catch (e) {
    console.error('Error cleaning redirect URL:', e);
    // Return the original URL if all else fails
    return url.startsWith('/') ? url : `/${url}`;
  }
};

const useAuth = (): UseAuthReturn => {
  const user = useSelector(getUser) as User | null;
  const {
    history,
    isMatchPath,
    useSearchParams,
    directKeepSearch,
    openNewTab,
  } = useNavigate();
  const { setLoading } = useLoading();
  const dispatch = useDispatch();

  // Enhanced memoized values with proper typing
  const isLoggedIn = useMemo(
    () => Boolean(user?.is_logged_in),
    [user?.is_logged_in]
  );
  const currentBalance = useMemo(
    () => user?.balance?.contact_quota || 0,
    [user?.balance?.contact_quota]
  );
  const userPlan = useMemo(() => user?.plan || null, [user?.plan]);
  const buyerData = useMemo(() => user?.buyer || null, [user?.buyer]);
  const supplierData = useMemo(() => user?.supplier || null, [user?.supplier]);
  const balance = useMemo(() => user?.balance || null, [user?.balance]);

  const isPaymentUser = useMemo(() => {
    if (!user) return false;

    let isPayed = false;

    // For buyers, check if business is verified
    if (user.buyer?.plan?.is_biz_verified === 1) {
      isPayed = true;
    }

    // For suppliers, check if they have an active plan
    const planName = user.supplier?.plan?.plan_name;
    if (planName === "Starter" || planName === "Advance") {
      isPayed = true;
    }

    return isPayed;
  }, [user]);

  const getLoginUrl = useMemo(() => {
    const currentUrl = window.location.href;
    return addUtmParam(
      `${SOURCING_DOMAIN}${PATHS.login}?${
        SEARCH_PARAMS.directUrl
      }=${encodeBase64(currentUrl)}`
    );
  }, []);

  const onDirectLogin = useCallback(() => {
    const currentUrl = window.location.href;
    const directUrl = useSearchParams.get(SEARCH_PARAMS.directUrl);
    
    // Always encode the URL consistently
    const urlToRedirect = directUrl || currentUrl;
    const encodedUrl = encodeBase64(urlToRedirect);
    
    directKeepSearch(
      PATHS.login,
      `${SEARCH_PARAMS.directUrl}=${encodedUrl}`
    );
  }, [useSearchParams, directKeepSearch]);

  const onDirectSignup = useCallback(() => {
    const currentUrl = window.location.href;
    const directUrl = useSearchParams.get(SEARCH_PARAMS.directUrl);
    
    // Always encode the URL consistently
    const urlToRedirect = directUrl || currentUrl;
    const encodedUrl = encodeBase64(urlToRedirect);
    
    history.push({
      pathname: PATHS.register,
      search: `${SEARCH_PARAMS.directUrl}=${encodedUrl}`,
    });
  }, [history, useSearchParams]);

  const onClickLoginSupplier = useCallback(() => {
    openNewTab(
      `${PATHS.login}?${SEARCH_PARAMS.directUrl}=${encodeBase64(
        SOURCING_DOMAIN
      )}`
    );
  }, [openNewTab]);

  const onLogout = useCallback(async () => {
    setLoading(true);
    try {
      await apiLogoutUser();
      dispatch(logoutUser());
      dispatch(setChatList([]));

      const matchDirectHome =
        isMatchPath("/user") ||
        isMatchPath(PATHS.paymentBuyer) ||
        isMatchPath(PATHS.paymentSupplier);

      history.push(matchDirectHome ? PATHS.home : PATHS.buyleads);
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, setLoading, isMatchPath, history]);

  const onGetUserInformation = useCallback(async () => {
    try {
      const response = await apiGetMe();
      const newUserData = response?.data?.message?.data || {};
      dispatch(
        setUser({
          ...newUserData,
          freshdi_customer_id: newUserData?.name,
          userStatus: "fulfilled",
        })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch, setLoading]);

  const onDirectNextPageOfLogin = useCallback(() => {
    const directPath = useSearchParams.get(SEARCH_PARAMS.directUrl);
    if (directPath) {
      try {
        const cleanPath = cleanRedirectUrl(directPath);
        console.log('Redirecting to:', cleanPath); // Debug log
        
        // Validate that the path is safe and exists
        if (cleanPath && cleanPath.startsWith('/')) {
          history.push(cleanPath);
        } else {
          console.warn('Invalid redirect path:', cleanPath);
          history.push(PATHS.home);
        }
      } catch (error) {
        console.error('Error processing redirect:', error);
        history.push(PATHS.home);
      }
    } else {
      history.push(PATHS.home);
    }
  }, [history, useSearchParams]);

  return {
    isLoggedIn,
    currentBalance,
    isPaymentUser,
    getLoginUrl,
    user,
    userPlan,
    buyerData,
    supplierData,
    balance,
    onDirectLogin,
    onDirectSignup,
    onLogout,
    onGetUserInformation,
    onDirectNextPageOfLogin,
    onClickLoginSupplier,
  };
};

export default useAuth;